import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AppDispatch } from '../../store/store';
import { registerUser } from '../../store/UserSlice';
import { registerSchema, RegisterFormData } from '../../schemas/auth.schema';
import Input from '../../components/form/Input';
import Button from '../../components/form/Button';
import { ArrowLeft } from 'heroicons-react';

export default function Register() {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      isAdmin: false,
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: RegisterFormData) => {
    const { confirmPassword, isAdmin, ...userPayloadWithoutConfirmPassword } = data;

    const userPayloadWithAdmin = {
      ...userPayloadWithoutConfirmPassword,
      isAdmin: isAdmin || false,
    };

    try {
      const result = await dispatch(registerUser(userPayloadWithAdmin));

      if (registerUser.fulfilled.match(result)) {
        const isSuccess = result.payload;

        if (isSuccess) {
          toast.success('Registered successfully');
          navigate('/login');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Registration failed. Please try again.');
    }
  };


  return (

    <div className="flex flex-col lg:flex-row items-center">
      <div className="hidden lg:flex w-1/2">
        <img src='./images/loginpic.jpg' className='object-cover w-full h-screen' alt="sideimage" />
      </div>
      <div className="w-full lg:w-1/2 p-8 min-h-screen flex flex-col justify-center relative" >
        <div className="absolute top-8 left-8">
          <Link to="/" className="text-[#037897] font-gilroy-medium text-sm flex items-center">
            <ArrowLeft className="w-4 inline-block mr-2" /> Back to Home
          </Link>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className='max-w-2xl m-auto'>
            <div className='text-center'>
              <Link to="/" className="inline-block">
                <img src='./images/bluelogo.png' alt="Logo" className='mx-auto mb-6' />
              </Link>
              <h2 className='mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]'>Create your account</h2>
              <p className='text-gray-7 text-[#78909C] font-gilroy text-[18px]'>Create your account to access personalized healthcare services.</p>
            </div>

            <div className='pt-4 lg:pt-8'>
              <Input
                id="fullName"
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                autoComplete="name"
                error={errors.fullName?.message}
                {...register("fullName")}
              />
            </div>

            <div className='pt-4 lg:pt-8'>
              <Input
                id="email"
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                autoComplete="email"
                error={errors.email?.message}
                {...register("email")}
              />
            </div>


            <div className='pt-4 lg:pt-8'>
              <Input
                id="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="new-password"
                showPasswordToggle
                error={errors.password?.message}
                {...register("password")}
              />
              {!errors.password?.message && (
                <p className="text-[#78909C] text-sm mt-1">Password must be between 8 and 20 characters</p>
              )}
            </div>

            <div className='pt-4 lg:pt-8'>
              <Input
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                placeholder="Confirm your password"
                autoComplete="new-password"
                showPasswordToggle
                error={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />
            </div>
            <Button
              isLoading={isSubmitting}
              loadingText="Registering..."
            >
              Register
            </Button>

            <h4 className='p-4 mt-4 text-center'>Already have an account? <Link to='/login' className='text-[#037897]'>Login</Link></h4>
          </div>
        </form>
      </div>
    </div>
  );
}
